import React from 'react';
import { Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { Site } from '../../../../types/firebase';

interface SiteConditionCardProps {
  sites: Site[];
  siteStatusMap: Record<string, 'healthy' | 'alert'>;
}

/** Site status is "alert" when at least one scan in the last 30 days detected a skimmer at that site (see useFeedData). */
export const SiteConditionCard: React.FC<SiteConditionCardProps> = ({
  sites,
  siteStatusMap,
}) => {
  const navigate = useNavigate();
  const alertSites = sites.filter((s) => siteStatusMap[s.id] === 'alert');
  const healthyCount = sites.length - alertSites.length;
  const firstAlert = alertSites[0];
  const otherSites = sites.filter((s) => s.id !== firstAlert?.id);
  const listSites = otherSites.slice(0, 5);

  return (
    <Box className="glass-panel data-card site-condition-card">
      <h3 className="card-title">Site Condition Monitor</h3>
      <p className="site-condition-subtitle">
        Skimmer status by location · based on scans in the last 30 days
      </p>
      <div className="site-condition-summary">
        <span className="summary-cyan">{healthyCount} healthy</span>
        <span className="summary-divider">·</span>
        <span className="summary-red">{alertSites.length} need attention</span>
      </div>

      <div className="sites-list">
        {firstAlert && (
          <div
            className="site-box border-red alert-box"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/sites/${firstAlert.id}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/sites/${firstAlert.id}`)}
            style={{ cursor: 'pointer' }}
          >
            <div className="alert-header">
              <WarningAmberIcon sx={{ fontSize: 16, color: '#ff3b3b' }} />
              <span className="alert-badge">Needs attention</span>
            </div>
            <div className="alert-details">
              <p className="alert-site-name">{firstAlert.name}</p>
              <p className="alert-reason">At least one skimmer detected in the last 30 days. Review scans and take action.</p>
            </div>
          </div>
        )}

        {listSites.map((site) => {
          const isAlert = siteStatusMap[site.id] === 'alert';
          return (
            <Box
              key={site.id}
              className={`site-box ${isAlert ? 'border-red' : 'border-cyan'}`}
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/sites/${site.id}`)}
            >
              <div className="site-icon-container">
                {isAlert ? (
                  <WarningAmberIcon sx={{ fontSize: 22, color: '#ff3b3b' }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#22d3ee' }} />
                )}
              </div>
              <div className="site-info">
                <div className="site-name">{site.name}</div>
                <div className={`site-status-label ${isAlert ? 'status-alert' : 'status-healthy'}`}>
                  {isAlert ? 'Needs attention' : 'Healthy'}
                </div>
              </div>
            </Box>
          );
        })}
      </div>

      <div
        className="critical-management-btn mt-auto"
        onClick={() => navigate('/sites')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && navigate('/sites')}
      >
        <div className="btn-left">
          <ScheduleIcon sx={{ fontSize: 18, color: '#6b7280' }} />
          <div className="btn-text">
            <h4>View all sites</h4>
            <span>Manage locations and review scans</span>
          </div>
        </div>
        <ChevronRightIcon sx={{ fontSize: 18, color: '#6b7280' }} />
      </div>
    </Box>
  );
};
